// src/services/authApi.js
// Autenticación Ecolab y llamadas admin

const BASE = process.env.REACT_APP_API_URL || 'http://localhost:5246';

// ── Estado de sesión en memoria ──────────────────────────────────────────
let _token = localStorage.getItem('ecolab_token') || null;
let _user  = null;
try { _user = JSON.parse(localStorage.getItem('ecolab_user') || 'null'); } catch {}

// Verificar si el JWT guardado en localStorage ya expiró.
// Si expiró, limpiar la sesión para que isLogged() devuelva false
// y el usuario sea redirigido al login en vez de entrar al portal.
function jwtEstaExpirado(token) {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    if (!payload.exp) return false;
    return payload.exp * 1000 < Date.now();
  } catch { return true; } // si no parsea, tratarlo como expirado
}
if (_token && jwtEstaExpirado(_token)) {
  _token = null; _user = null;
  localStorage.removeItem('ecolab_token');
  localStorage.removeItem('ecolab_user');
}

export const getToken  = () => _token;
export const getUser   = () => _user;
export const isLogged  = () => !!_token;
export const isAdmin   = () => {
  const roles = _user?.roles || [];
  return roles.some(r => [
    'SuperAdmin','AdminConfederacion','AdminFederacion','AdminCooperativa',
    'Auditor','Validador','Orientador',
  ].includes(r));
};

// Permisos granulares por tab del panel admin
const PERMISOS_TABS = {
  dashboard:    ['SuperAdmin','AdminConfederacion','AdminFederacion','AdminCooperativa','Auditor','Validador'],
  usuarios:     ['SuperAdmin','AdminConfederacion'],
  afiliaciones: ['SuperAdmin','AdminConfederacion','AdminFederacion','AdminCooperativa','Auditor','Validador','Orientador'],
  mapa:         ['SuperAdmin','AdminConfederacion','AdminFederacion','AdminCooperativa','Auditor'],
  aliados:      ['SuperAdmin','AdminConfederacion','AdminFederacion','Auditor'],
  integraciones:['SuperAdmin'],
};

export const puedeVerTab = (tabId) => {
  const roles = _user?.roles || [];
  const permitidos = PERMISOS_TABS[tabId] || [];
  return roles.some(r => permitidos.includes(r));
};

export const esOrientador = () => {
  const roles = _user?.roles || [];
  return roles.includes('Orientador') &&
    !roles.some(r => ['SuperAdmin','AdminConfederacion','AdminFederacion','AdminCooperativa','Auditor','Validador'].includes(r));
};

export const puedeAprobarRechazar = () => {
  const roles = _user?.roles || [];
  return roles.some(r => ['SuperAdmin','AdminConfederacion','AdminFederacion','AdminCooperativa','Auditor','Validador'].includes(r));
};

export const puedeEliminarCompleto = () => {
  const roles = _user?.roles || [];
  return roles.some(r => ['SuperAdmin','AdminConfederacion'].includes(r));
};

// Solo SuperAdmin puede editar datos sensibles de los afiliados (cédula, email,
// nombre, etc.). Ver AfiliadosEcolabController.Update — el backend también lo valida.
export const esSuperAdmin = () => {
  const roles = _user?.roles || [];
  return roles.includes('SuperAdmin');
};

function setSession(token, user) {
  _token = token; _user = user;
  localStorage.setItem('ecolab_token', token);
  localStorage.setItem('ecolab_user', JSON.stringify(user));
}

/**
 * Actualiza campos del usuario en memoria y localStorage SIN cambiar el token.
 * Útil para cuando el usuario cambia su foto, perfil, etc. y queremos
 * que el Navbar y otros componentes que leen de localStorage se enteren.
 * Dispara 'ecolab_session_change' para que Navbar reaccione en la misma pestaña.
 */
export function updateUserLocal(partial) {
  if (!_user) {
    try { _user = JSON.parse(localStorage.getItem('ecolab_user') || 'null'); } catch {}
  }
  if (!_user) return;
  _user = { ..._user, ...partial };
  localStorage.setItem('ecolab_user', JSON.stringify(_user));
  window.dispatchEvent(new Event('ecolab_session_change'));
}

export function clearSession() {
  // Disparar evento para que Navbar reaccione inmediatamente en la misma pestaña
  window.dispatchEvent(new Event('ecolab_session_change'));
  _token = null; _user = null;
  localStorage.removeItem('ecolab_token');
  localStorage.removeItem('ecolab_user');
}

async function authFetch(path, options = {}) {
  const res = await fetch(`${BASE}${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(options.headers || {}),
      ..._token ? { Authorization: `Bearer ${_token}` } : {},
    },
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw Object.assign(new Error(data.mensaje || 'Error'), { data, status: res.status });
  return data;
}

// ── Auth ─────────────────────────────────────────────────────────────────

export async function login(email, password, turnstileToken) {
  const data = await authFetch('/api/ecolab/auth/login', {
    method: 'POST',
    body: JSON.stringify({ email, password, turnstileToken }),
  });
  setSession(data.token, data.usuario);
  return data;
}

export function logout() {
  clearSession();
}

// ── Recuperación de contraseña ───────────────────────────────────────────

// Solicita un email con link de recuperación.
// Por seguridad el backend SIEMPRE responde 200 sin revelar si el email existe.
export async function solicitarRecuperarPassword(email, turnstileToken) {
  return authFetch('/api/ecolab/auth/forgot-password', {
    method: 'POST',
    body: JSON.stringify({ email, turnstileToken }),
  });
}

// Confirma el reset con el token recibido por email + nueva contraseña.
export async function confirmarRecuperarPassword(email, token, newPassword) {
  return authFetch('/api/ecolab/auth/reset-password', {
    method: 'POST',
    body: JSON.stringify({ email, token, newPassword }),
  });
}

// ── Admin: afiliaciones ──────────────────────────────────────────────────

export const getAfiliacionesPendientes = (params = {}, page = 1, pageSize = 25) => {
  const q = new URLSearchParams({ page, pageSize, ...params }).toString();
  return authFetch(`/api/ecolab/admin/afiliaciones-pendientes?${q}`);
};

export const aprobarAfiliacion = (id) =>
  authFetch(`/api/ecolab/admin/afiliaciones-pendientes/${id}/aprobar`, { method: 'PUT' });

export const rechazarAfiliacion = (id, motivo = '') =>
  authFetch(`/api/ecolab/admin/afiliaciones-pendientes/${id}/rechazar`, {
    method: 'PUT', body: JSON.stringify({ motivo }),
  });

export const reevaluarAfiliacion = (id, motivo = '') =>
  authFetch(`/api/ecolab/admin/afiliaciones-pendientes/${id}/reevaluar`, {
    method: 'PUT', body: JSON.stringify({ motivo }),
  });

// Reactivación de solicitudes ABANDONADA (SuperAdmin): causa + diagnóstico, y acción.
export const getInfoReactivacion = (id) =>
  authFetch(`/api/ecolab/admin/afiliaciones-pendientes/${id}/reactivacion`);
export const reactivarSolicitudAbandonada = (id, motivo = '') =>
  authFetch(`/api/ecolab/admin/afiliaciones-pendientes/${id}/reactivar`, {
    method: 'POST', body: JSON.stringify({ motivo }),
  });

// Recalcular estado de una solicitud atascada (SuperAdmin) — queda en auditoría.
export const recalcularEstadoSolicitud = (id, motivo = '') =>
  authFetch(`/api/ecolab/documentos-afiliacion/${id}/recalcular-estado`, {
    method: 'POST', body: JSON.stringify({ motivo }),
  });

// Confirmación de correo de usuarios (admin)
export const reenviarConfirmacionUsuario = (id) =>
  authFetch(`/api/ecolab/admin/usuarios/${id}/reenviar-confirmacion`, { method: 'POST' });
export const confirmarEmailUsuario = (id) =>
  authFetch(`/api/ecolab/admin/usuarios/${id}/confirmar-email`, { method: 'POST' });

export const eliminarSesionOrientacion = (id, motivo = '') =>
  authFetch(`/api/ecolab/admin/afiliaciones-pendientes/${id}/sesion-orientacion`, {
    method: 'DELETE', body: JSON.stringify({ motivo }),
  });

// Activar acceso al portal del afiliado (paso final admin tras completar flujo)
export const activarAccesoAfiliado = (id, motivo = '') =>
  authFetch(`/api/ecolab/afiliados/${id}/activar-acceso`,
    { method: 'POST', body: JSON.stringify({ motivo }) });

// Edición administrativa de datos del afiliado (solo SuperAdmin — backend valida).
// Cambios en campos LLAVE (cédula, email, nombre, apellidos, teléfono, fecha nacim,
// género, tipo documento) requieren 'motivo' no vacío, se propagan al AspNetUsers
// vinculado y a TODAS las demás afiliaciones del mismo usuario. Si cambia email
// se marca EmailConfirmed=false y se reenvía confirmación. Si cambia documento
// los documentos aprobados vuelven a PENDIENTE para re-verificación.
export const actualizarAfiliado = (id, dto) =>
  authFetch(`/api/ecolab/afiliados/${id}`,
    { method: 'PUT', body: JSON.stringify(dto) });

// Confirmar email del afiliado (paso 1.5 del flujo)
export const confirmarEmail = (email, token) =>
  authFetch('/api/ecolab/auth/confirmar-email',
    { method: 'POST', body: JSON.stringify({ email, token }) });

// Reenviar el correo de confirmación (rate-limited a 1/min por email en el backend)
export const reenviarConfirmacionEmail = (email, turnstileToken) =>
  authFetch('/api/ecolab/auth/reenviar-confirmacion-email',
    { method: 'POST', body: JSON.stringify({ email, turnstileToken }) });

// Obtener la sitekey de Turnstile del backend (público)
export const getTurnstileSiteKey = () =>
  authFetch('/api/ecolab/auth/turnstile-sitekey');

export const eliminarAfiliacionCompleta = (id, motivo = '') =>
  authFetch(`/api/ecolab/admin/afiliaciones-pendientes/${id}/eliminar-completo`, {
    method: 'DELETE', body: JSON.stringify({ motivo }),
  });

// ── Cooperativas admin ───────────────────────────────────────────────────────
export const reasignarCooperativa = (afiliadoId, cooperativaId, motivo = '') =>
  authFetch(`/api/ecolab/afiliados/${afiliadoId}/cooperativa`, {
    method: 'PATCH', body: JSON.stringify({ cooperativaID: cooperativaId, motivo }),
  });

export const getCooperativasAdmin = (params = {}) => {
  const q = new URLSearchParams(Object.fromEntries(Object.entries(params).filter(([,v])=>v!==undefined))).toString();
  return authFetch(`/api/ecolab/cooperativas?${q}`);
};

export const crearCooperativa = (dto) =>
  authFetch('/api/ecolab/cooperativas', { method:'POST', body: JSON.stringify(dto) });

export const actualizarCooperativa = (id, dto) =>
  authFetch(`/api/ecolab/cooperativas/${id}`, { method:'PUT', body: JSON.stringify(dto) });

export const cambiarEstadoCooperativa = (id, activo) =>
  authFetch(`/api/ecolab/cooperativas/${id}/estado`, { method:'PATCH', body: JSON.stringify({ activo }) });

// Aprobar documentos pendientes de una cooperativa (sube la cédula del rep).
export const aprobarDocumentosCooperativa = (id, urlCedulaRepresentante) =>
  authFetch(`/api/ecolab/cooperativas/${id}/aprobar-documentos`,
    { method:'POST', body: JSON.stringify({ urlCedulaRepresentante }) });

// ── Aplicaciones (SSO cross-app por audience JWT) ───────────────────────────
export const getMisAplicaciones = () =>
  authFetch('/api/ecolab/aplicaciones/mis-aplicaciones');
export const getCatalogoAplicaciones = () =>
  authFetch('/api/ecolab/aplicaciones/catalogo');
export const verificarAccesoApp = (appCode) =>
  authFetch(`/api/ecolab/aplicaciones/verificar-acceso?appCode=${encodeURIComponent(appCode)}`);
export const getAppsUsuarioAdmin = (userId) =>
  authFetch(`/api/ecolab/aplicaciones/admin/apps-usuario/${encodeURIComponent(userId)}`);
export const otorgarAppUsuario = (usuarioID, appCode, motivo) =>
  authFetch('/api/ecolab/aplicaciones/admin/apps-usuario/otorgar',
    { method:'POST', body: JSON.stringify({ usuarioID, appCode, motivo }) });
export const revocarAppUsuario = (usuarioID, appCode, motivo) =>
  authFetch('/api/ecolab/aplicaciones/admin/apps-usuario/revocar',
    { method:'POST', body: JSON.stringify({ usuarioID, appCode, motivo }) });

// ── Notificaciones in-app (campanita) ───────────────────────────────────────
export const getNotificaciones = ({ soloNoLeidas = false, limite = 50 } = {}) => {
  const q = new URLSearchParams({ soloNoLeidas: String(soloNoLeidas), limite: String(limite) }).toString();
  return authFetch(`/api/ecolab/notificaciones?${q}`);
};
export const getNotificacionesCount = () =>
  authFetch('/api/ecolab/notificaciones/count');
export const marcarNotificacionLeida = (id) =>
  authFetch(`/api/ecolab/notificaciones/${id}/leer`, { method:'POST' });
export const marcarTodasNotificacionesLeidas = () =>
  authFetch('/api/ecolab/notificaciones/leer-todas', { method:'POST' });

// ── Pagos de inscripción (admin) ─────────────────────────────────────────────
export const getListaPagosInscripcion = (params = {}) => {
  const q = new URLSearchParams(Object.fromEntries(Object.entries(params).filter(([,v])=>v!==undefined))).toString();
  return authFetch(`/api/ecolab/pago-inscripcion/lista?${q}`);
};

export const confirmarPagoManual = (afiliadoId, dto) =>
  authFetch(`/api/ecolab/pago-inscripcion/confirmar-manual/${afiliadoId}`, {
    method: 'POST', body: JSON.stringify(dto),
  });

export const getListaExclusion = (params = {}) => {
  const q = new URLSearchParams(Object.fromEntries(Object.entries(params).filter(([,v])=>v!==undefined))).toString();
  return authFetch(`/api/ecolab/admin/lista-exclusion?${q}`);
};

export const agregarExclusion = (dto) =>
  authFetch('/api/ecolab/admin/lista-exclusion', { method:'POST', body: JSON.stringify(dto) });

export const desactivarExclusion = (id) =>
  authFetch(`/api/ecolab/admin/lista-exclusion/${id}`, { method:'DELETE' });

export const getConfigPasarela = () =>
  authFetch('/api/ecolab/pago-inscripcion/config');

export const updateConfigPasarela = (dto) =>
  authFetch('/api/ecolab/pago-inscripcion/config', { method:'PUT', body: JSON.stringify(dto) });

export const getAccionesAuditoria = () =>
  authFetch('/api/ecolab/admin/auditoria/acciones-disponibles');

export const getAuditoriaAfiliados = (params = {}) => {
  const q = new URLSearchParams(Object.fromEntries(Object.entries(params).filter(([,v])=>v!==undefined))).toString();
  return authFetch(`/api/ecolab/admin/auditoria/afiliados?${q}`);
};

export const getAuditoriaUsuarios = (params = {}) => {
  const q = new URLSearchParams(Object.fromEntries(Object.entries(params).filter(([,v])=>v!==undefined))).toString();
  return authFetch(`/api/ecolab/admin/auditoria/usuarios?${q}`);
};

// ── Admin: usuarios ──────────────────────────────────────────────────────

export const getUsuarios = (params = {}) => {
  const q = new URLSearchParams(params).toString();
  return authFetch(`/api/ecolab/admin/usuarios?${q}`);
};
export const getUsuarioById    = (id)           => authFetch(`/api/ecolab/admin/usuarios/${id}`);
export const getKpis           = ()             => authFetch('/api/ecolab/admin/usuarios/kpis');
export const getMapaAfiliados  = (params = {})  => {
  const q = new URLSearchParams(params).toString();
  return authFetch(`/api/ecolab/admin/usuarios/mapa?${q}`);
};
export const getRolesDisponibles = ()           => authFetch('/api/ecolab/admin/usuarios/roles-disponibles');
export const cambiarEstadoUsuario = (id, activo) =>
  authFetch(`/api/ecolab/admin/usuarios/${id}/estado`, { method:'PUT', body: JSON.stringify({ activo }) });
export const cambiarRolUsuario = (id, nuevoRol) =>
  authFetch(`/api/ecolab/admin/usuarios/${id}/rol`, { method:'PUT', body: JSON.stringify({ nuevoRol }) });

export const actualizarRolesUsuario = (id, rolesAgregar = [], rolesQuitar = []) =>
  authFetch(`/api/ecolab/admin/usuarios/${id}/rol`, {
    method:'PUT',
    headers:{'Content-Type':'application/json'},
    body: JSON.stringify({ rolesAgregar, rolesQuitar }),
  });
export const cambiarPasswordUsuario = (id, nuevoPassword) =>
  authFetch(`/api/ecolab/admin/usuarios/${id}/password`, { method:'PUT', body: JSON.stringify({ nuevoPassword }) });

// ── Admin: detalle y edición de afiliados ────────────────────────────────

export const getDetalleAfiliado = (id) =>
  authFetch(`/api/ecolab/admin/afiliaciones-pendientes/${id}`);

export const editarUsuario = (id, dto) =>
  authFetch(`/api/ecolab/admin/usuarios/${id}/editar`, {
    method: 'PUT',
    body: JSON.stringify(dto),
  });

// Retorna la URL del proxy seguro del backend para ver un documento.
// El backend descarga el archivo de GCS y lo transmite — la URL de Storage
// nunca llega al navegador. El frontend llama a esta URL con Bearer token.
export const getUrlFirmadaDocumento = (gsUri) => {
  const url = `${BASE}/api/ecolab/verificar-documento/proxy-documento?gsUri=${encodeURIComponent(gsUri)}`;
  return Promise.resolve({ url, token: _token });
};

// ── Documentos de afiliación ──────────────────────────────────────────────
export const guardarDocumentoAfiliado = (dto) =>
  fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5246'}/api/ecolab/documentos-afiliacion`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(dto),
  }).then(r => r.json());

export const getDocumentosAfiliado = (afiliadoId) =>
  authFetch(`/api/ecolab/documentos-afiliacion/${afiliadoId}`);

export const revisarDocumento = (docId, dto) =>
  authFetch(`/api/ecolab/documentos-afiliacion/${docId}/revisar`, {
    method: 'PUT', headers: {'Content-Type':'application/json'}, body: JSON.stringify(dto),
  });

export const getSesionesAfiliado = (afiliadoId) =>
  authFetch(`/api/ecolab/documentos-afiliacion/${afiliadoId}/sesiones`);

export const registrarSesionOrientacion = (afiliadoId, dto) =>
  authFetch(`/api/ecolab/documentos-afiliacion/${afiliadoId}/sesiones`, {
    method: 'POST', headers: {'Content-Type':'application/json'}, body: JSON.stringify(dto),
  });

// ── Calendario orientadores ───────────────────────────────────────────────
export const getOrientadores = () =>
  authFetch('/api/ecolab/calendario/orientadores');

export const getSlotsOrientador = (orientadorId, dias = 7, duracionMin = 60, fechaInicio = null) => {
  const params = new URLSearchParams({ dias, duracionMin });
  if (fechaInicio) params.append('fechaInicio', fechaInicio);
  return authFetch(`/api/ecolab/calendario/slots/${orientadorId}?${params}`);
};

export const agendarSesionConMeet = (dto) =>
  authFetch('/api/ecolab/calendario/agendar', {
    method: 'POST', headers: {'Content-Type':'application/json'}, body: JSON.stringify(dto),
  });

export const setCalendarIdOrientador = (orientadorId, calendarId) =>
  authFetch(`/api/ecolab/calendario/orientadores/${orientadorId}/calendar-id`, {
    method: 'PUT', headers: {'Content-Type':'application/json'}, body: JSON.stringify({ calendarId }),
  });

export const crearUsuarioAdmin = (dto) =>
  authFetch('/api/ecolab/admin/usuarios', {
    method: 'POST', headers: {'Content-Type':'application/json'}, body: JSON.stringify(dto),
  });

export const gestionarSesion = (sesionId, dto) =>
  authFetch(`/api/ecolab/documentos-afiliacion/sesiones/${sesionId}/gestionar`, {
    method: 'PUT', headers: {'Content-Type':'application/json'}, body: JSON.stringify(dto),
  });

export const getMisSesionesOrientador = () =>
  authFetch('/api/ecolab/calendario/mis-sesiones');
